import { useNavigate } from 'react-router-dom';
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { CircleAlert, FileArchive, ShieldCheck } from 'lucide-react';
import { Banner, Checkbox, Field, Input, Select } from '@/components/ui';
import { useNodes } from '@/hooks/useNodes';
import { useCreateFilesSite } from '@/hooks/useSites';
import { toast } from '@/store/toastStore';
import { errorCode, errorField, errorText } from '@/utils/errors';
import { countryWithFlag, languageWithFlag } from '@/utils/market';
import { idempotencyKey } from '@/utils/idempotency';
import { isDomainName } from '@/utils/validation';

/** The panel's own address is behind a proxy that takes about a hundred megabytes a request. */
const LARGEST_ARCHIVE = 95 * 1024 ** 2;
const ARCHIVE = /\.(?:zip|tar|tgz|tar\.gz)$/i;
const COUNTRY = /^[A-Z]{2}$/;
const LANGUAGE = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;

type Tier = 'production' | 'staging';

/**
 * A new site of files on an external server (ADR-0044): no CMS, whoever makes
 * its pages. The archive of its files, or a starter page until they come; the
 * server it goes on; where there, when the server's own layout is not wanted.
 * Rendered inside the new-site drawer, which owns the buttons.
 */
export function FilesSiteForm({
  formId,
  header,
  open,
  onBusy,
  onDone,
}: {
  readonly formId: string;
  /** What the form starts with: the drawer's choice of what kind of site. */
  readonly header: ReactNode;
  readonly open: boolean;
  readonly onBusy: (busy: boolean) => void;
  readonly onDone: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [primaryDomain, setPrimaryDomain] = useState('');
  const [www, setWww] = useState(true);
  const [hostNodeId, setHostNodeId] = useState('');
  const [tier, setTier] = useState<Tier>('production');
  const [archive, setArchive] = useState<File | null>(null);
  const [targetRoot, setTargetRoot] = useState('');
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState('');
  const [shown, setShown] = useState(false);
  const key = useRef(idempotencyKey('site.create_files'));
  const uploadId = useRef(crypto.randomUUID());

  const nodes = useNodes({ enabled: open });
  const hosts = (nodes.data ?? []).filter((node) => node.role === 'host' && node.agentVersion !== null);
  const chosenHost = hostNodeId || hosts[0]?.id || '';

  const domain = primaryDomain.trim().toLowerCase();
  const countryCode = country.trim().toUpperCase();
  const languageTag = language.trim();
  const rootDir = targetRoot.trim().replace(/\/+$/, '');
  const problems: Partial<Record<'displayName' | 'primaryDomain' | 'hostNodeId' | 'archive' | 'targetRoot' | 'country' | 'language', string>> = {};
  if (!displayName.trim()) problems.displayName = 'Нужно название';
  if (!isDomainName(domain)) problems.primaryDomain = 'Не похоже на доменное имя';
  if (!chosenHost) problems.hostNodeId = 'Сначала подключите внешний сервер с агентом';
  if (archive && !ARCHIVE.test(archive.name)) problems.archive = 'Нужен архив .zip, .tar или .tar.gz';
  else if (archive && archive.size > LARGEST_ARCHIVE) problems.archive = 'Архив больше 95 МБ: загрузите файлы на сервер вручную и создайте сайт без архива';
  if (rootDir && (!/^\/[A-Za-z0-9._\-/]+$/.test(rootDir) || rootDir.split('/').includes('..'))) problems.targetRoot = 'Полный путь каталога: /var/www/example.com';
  if (countryCode && !COUNTRY.test(countryCode)) problems.country = 'Код страны из двух латинских букв: DE, JP, BR';
  if (languageTag && !LANGUAGE.test(languageTag)) problems.language = 'Код языка: de, ja, pt-BR';

  const create = useCreateFilesSite({
    onSuccess: (result) => {
      toast.success('Создание сайта поставлено в очередь');
      key.current = idempotencyKey('site.create_files');
      uploadId.current = crypto.randomUUID();
      onBusy(false);
      onDone();
      navigate(`/sites/${result.siteId}`);
    },
    onError: () => onBusy(false),
  });
  const field = create.isError ? errorField(create.error) : null;
  const code = create.isError ? errorCode(create.error) : null;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setShown(true);
    if (Object.keys(problems).length > 0 || create.isPending) return;
    onBusy(true);
    create.mutate({
      input: {
        displayName: displayName.trim(),
        primaryDomain: domain,
        www,
        hostNodeId: chosenHost,
        tier,
        ...(rootDir ? { targetRoot: rootDir } : {}),
        ...(countryCode ? { country: countryCode } : {}),
        ...(languageTag ? { language: languageTag } : {}),
      },
      archive,
      uploadId: uploadId.current,
      key: key.current,
    });
  };

  const known = (value: string, name: string | null, fallback: string) => (value ? (name ?? 'код не распознан — проверьте') : fallback);

  return (
    <form id={formId} className="stack" onSubmit={submit} noValidate>
      {header}
      <Field label="Название" htmlFor="files-name" {...(shown && problems.displayName ? { error: problems.displayName } : {})}>
        <Input id="files-name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} maxLength={160} autoComplete="off" />
      </Field>

      <Field
        label="Домен"
        htmlFor="files-domain"
        {...(shown && problems.primaryDomain ? { error: problems.primaryDomain } : {})}
        {...(code === 'DOMAIN_ALREADY_BOUND' ? { error: 'Домен занят другим сайтом панели' } : {})}
      >
        <Input id="files-domain" className="mono" value={primaryDomain} onChange={(event) => setPrimaryDomain(event.target.value)} placeholder="example.com" spellCheck={false} autoComplete="off" />
      </Field>
      <Checkbox checked={www} onChange={setWww} label={`Отвечать и на www.${domain || 'домен'}`} />

      <div className="form-grid">
        <Field
          label="Сервер"
          htmlFor="files-host"
          hint="Внешний сервер с nginx или Apache"
          {...(shown && problems.hostNodeId ? { error: problems.hostNodeId } : {})}
          {...(field === 'hostNodeId' ? { error: 'Агент на сервере не подключён' } : {})}
        >
          <Select id="files-host" label="Сервер" value={chosenHost} onChange={setHostNodeId} options={hosts.map((node) => ({ value: node.id, label: node.name }))} />
        </Field>
        <Field label="Среда" htmlFor="files-tier">
          <Select
            id="files-tier"
            label="Среда"
            value={tier}
            onChange={setTier}
            options={[
              { value: 'production', label: 'Рабочий', hint: 'Открыт для индексации' },
              { value: 'staging', label: 'Тестовый', hint: 'Закрыт от индексации' },
            ]}
          />
        </Field>
      </div>

      <Field
        label="Файлы сайта"
        htmlFor="files-archive"
        hint={archive ? `${archive.name} · ${(archive.size / 1024 ** 2).toFixed(1)} МБ` : 'Архив .zip, .tar или .tar.gz с index.html. Без архива создаётся стартовая страница'}
        {...(shown && problems.archive ? { error: problems.archive } : {})}
        {...(field === 'uploadId' ? { error: 'Архив не загружен — повторите' } : {})}
      >
        <input
          id="files-archive"
          type="file"
          className="input"
          accept=".zip,.tar,.tgz,.gz,application/zip,application/gzip,application/x-tar"
          onChange={(event) => setArchive(event.target.files?.[0] ?? null)}
        />
      </Field>

      <Field
        label="Каталог на сервере"
        htmlFor="files-root"
        hint="Необязательно. По умолчанию — рядом с другими сайтами сервера"
        {...(shown && problems.targetRoot ? { error: problems.targetRoot } : {})}
        {...(field === 'targetRoot' ? { error: errorText(create.error) } : {})}
      >
        <Input id="files-root" className="mono" value={targetRoot} onChange={(event) => setTargetRoot(event.target.value)} placeholder={domain ? `/var/www/${domain}` : ''} spellCheck={false} autoComplete="off" />
      </Field>

      <div className="form-grid">
        <Field label="Страна" htmlFor="files-country" hint={known(countryCode, countryWithFlag(countryCode), 'Код из двух букв. Необязательно')} {...(shown && problems.country ? { error: problems.country } : {})}>
          <Input id="files-country" className="mono" value={country} onChange={(event) => setCountry(event.target.value)} maxLength={2} autoComplete="off" spellCheck={false} />
        </Field>
        <Field label="Язык" htmlFor="files-language" hint={known(languageTag, languageWithFlag(languageTag), 'Код: de, ja, pt-BR. Необязательно')} {...(shown && problems.language ? { error: problems.language } : {})}>
          <Input id="files-language" className="mono" value={language} onChange={(event) => setLanguage(event.target.value)} maxLength={35} autoComplete="off" spellCheck={false} />
        </Field>
      </div>

      <Banner tone="info" icon={archive ? FileArchive : ShieldCheck}>
        <div className="stack stack-tight">
          <span>Сертификат сервера самоподписанный: в Cloudflare нужен режим SSL «Full».</span>
          <span>
            {archive ? 'Символические ссылки из архива не переносятся.' : 'Стартовая страница закрыта от индексации.'}
          </span>
        </div>
      </Banner>

      {create.isError && !['DOMAIN_ALREADY_BOUND'].includes(code ?? '') && !['hostNodeId', 'uploadId', 'targetRoot'].includes(field ?? '') ? (
        <p className="field-error" role="alert">
          <CircleAlert size={14} aria-hidden="true" />
          {errorText(create.error, 'Не удалось поставить создание в очередь')}
        </p>
      ) : null}
    </form>
  );
}
